import type { i18n as I18n } from 'i18next';
import { FeedType } from '@/domain/live';

/** Legacy UOF keys are stored as `br:<key>` in MarketTranslates / OutcomeTranslates. */
export function feedTranslationKey(key: string, feedType?: number | null): string {
  const trimmed = key.trim();
  if (!trimmed) return trimmed;
  if (feedType === FeedType.UOF && !trimmed.startsWith('br:')) {
    return `br:${trimmed}`;
  }
  return trimmed;
}

function resourceDictionary(i18n: I18n, namespace: string): Record<string, string> {
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const bundle = i18n.getResourceBundle(lng, namespace);
  if (bundle && typeof bundle === 'object') {
    return bundle as Record<string, string>;
  }

  for (const fallback of i18n.languages ?? []) {
    const fallbackBundle = i18n.getResourceBundle(fallback, namespace);
    if (fallbackBundle && typeof fallbackBundle === 'object') {
      return fallbackBundle as Record<string, string>;
    }
  }

  return {};
}

/**
 * Candidate keys for a feed translate lookup.
 *
 * UOF dictionaries are keyed as `br:<code>` (e.g. `br:2` = draw). Standard
 * outcomes reuse glyphs like away `2`, so falling through to `br:` for non-UOF
 * feeds mis-labels 1x2 as `1 / x / x`.
 */
export function lookupCandidates(key: string, feedType?: number | null): string[] {
  const trimmed = key.trim();
  if (!trimmed) return [];

  const withoutBr = trimmed.startsWith('br:') ? trimmed.slice(3) : trimmed;
  const withBr = trimmed.startsWith('br:') ? trimmed : `br:${trimmed}`;

  if (feedType === FeedType.UOF) {
    return [...new Set([withBr, withoutBr, trimmed])];
  }

  // Standard / unknown / LSports: never consult br: keys unless the caller
  // already passed an explicit br: code.
  if (trimmed.startsWith('br:')) {
    return [...new Set([trimmed, withoutBr])];
  }
  return [trimmed];
}

/**
 * Resolve a feed key against an i18next namespace loaded from the translate API.
 *
 * Looks up the resource bundle directly — i18next's default `nsSeparator` is `:`,
 * which breaks keys like `br:1` if passed through `t()` / `exists()`.
 */
export function translateNamespacedLabel(
  key: string,
  i18n: I18n,
  namespace: string,
  feedType?: number | null,
): string {
  const trimmed = key.trim();
  if (!trimmed) return trimmed;

  const dictionary = resourceDictionary(i18n, namespace);
  for (const candidate of lookupCandidates(trimmed, feedType)) {
    const translated = dictionary[candidate];
    if (typeof translated === 'string' && translated.length > 0) {
      return translated;
    }
  }

  return trimmed;
}
